import * as d3 from 'd3';

/**
 * Calculates responsive font sizes based on container width
 */
export function calculateResponsiveFontSizes(width: number): {
  strokeNumber: number;
  axisTick: number;
  axisLabel: number;
} {
  if (width < 400) {
    return {
      strokeNumber: 20, // Scaled down from 42px for very small containers
      axisTick: 9, // Scaled down from 12px
      axisLabel: 10, // Scaled down from 14px
    };
  }
  if (width < 600) {
    return {
      strokeNumber: 28,
      axisTick: 10,
      axisLabel: 11,
    };
  }
  if (width < 800) {
    return {
      strokeNumber: 35,
      axisTick: 11,
      axisLabel: 12,
    };
  }
  // Large screens (default)
  return {
    strokeNumber: 42,
    axisTick: 12,
    axisLabel: 14,
  };
}

/**
 * Formats time labels for D3 axis based on scale tick spacing
 * Determines appropriate decimal places to avoid duplicate labels
 */
export function formatTimeLabel(scale: d3.ScaleLinear<number, number>): (d: number) => string {
  const ticks = scale.ticks(10) as number[];
  let minStep = Infinity;
  for (let i = 1; i < ticks.length; i++) {
    const step = Math.abs(ticks[i] - ticks[i - 1]);
    if (step > 0 && step < minStep) minStep = step;
  }

  // Fallback if ticks are degenerate
  if (!isFinite(minStep) || minStep === 0) {
    return (d: number) => `${Math.round(d)}s`;
  }

  // Determine decimal places needed so adjacent ticks don't collapse to same label.
  // Examples:
  // - step = 1   -> 0 decimals
  // - step = 0.5 -> 1 decimal
  // - step = 0.1 -> 1 decimal
  // - step = 0.05 -> 2 decimals
  const decimals = Math.max(0, Math.min(3, Math.ceil(-Math.log10(minStep))));

  if (decimals === 0) {
    return (d: number) => `${Math.round(d)}s`;
  }

  return (d: number) => {
    // Avoid "-0.0s"
    const v = Math.abs(d) < 1e-9 ? 0 : d;
    return `${v.toFixed(decimals)}s`;
  };
}
